import React, { forwardRef, useImperativeHandle, useState } from "react";
import ReactDOM from "react-dom";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { Grow } from "@mui/material";

export type SnackbarSeverity = "success" | "error" | "warning" | "info";

export interface SnackbarMessage {
  key: number;
  message: string;
  severity: SnackbarSeverity;
}

export interface SnackbarRef {
  showSnackbar: (msg: string, sev?: SnackbarSeverity) => void;
}

const SnackbarAlert = forwardRef<SnackbarRef>((_, ref) => {
  const [snackPack, setSnackPack] = useState<SnackbarMessage[]>([]);

  useImperativeHandle(ref, () => ({
    showSnackbar(msg: string, sev: SnackbarSeverity = "success") {
      setSnackPack((prev) => [
        ...prev,
        { key: new Date().getTime(), message: msg, severity: sev },
      ]);
    },
  }));

  const handleClose =
    (key: number) =>
    (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === "clickaway") {
        return;
      }
      setSnackPack((prev) => prev.filter((snack) => snack.key !== key));
    };

  const snackbarContent = (
    <>
      {snackPack.map(({ key, message, severity }) => (
        <Snackbar
          key={key}
          open={true}
          autoHideDuration={2000}
          onClose={handleClose(key)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          TransitionComponent={Grow}
          sx={{
            zIndex: 9999, // Ensuring Snackbar is on top
          }}
        >
          <Alert
            severity={severity}
            sx={{ width: "100%", boxShadow: 6, zIndex: 9999 }} // Adding shadow and ensuring Alert is on top
            action={<React.Fragment></React.Fragment>}
          >
            {message}
          </Alert>
        </Snackbar>
      ))}
    </>
  );

  return ReactDOM.createPortal(snackbarContent, document.body);
});

export default SnackbarAlert;
